/**
 * /plaza/terms — Hustle Group Cooperative Terms of Service
 *
 * Doubles as the canonical Terms page for ALL Hustle Group platforms:
 *   HustleRewards · HustleConnect · HustlePlaza · HustleChat ·
 *   HustlePlate    · HustleRecipes · HustleGo      · HustleApps
 *
 * Content lifted (with minor structural framing) from the founder's brief
 * dated 01 Jun 2026. Tone: cooperative, transparent, no legal-ese.
 *
 * Linked from:
 *   • Plaza topbar nav  (Terms)
 *   • Plaza waitlist form  (small "By submitting you agree…")
 *   • Footer of every Plaza surface
 */
import { useEffect, useState } from "react";
import { Sun, Moon, ArrowLeft, ShieldCheck, BellRing, Users, MailMinus, Sparkles } from "lucide-react";
import "./HustleGroupTermsPage.css";

const LOGO_LIGHT =
  "https://customer-assets.emergentagent.com/job_mobile-resto/artifacts/d6gkfgwo_Local%20Hustle%20Logo%20-%20PLAZA%20LIGHT.png";
const LOGO_DARK =
  "https://customer-assets.emergentagent.com/job_mobile-resto/artifacts/ecrffi9b_Local%20Hustle%20Logo%20-%20PLAZA%20DARK.png";

const THEME_KEY = "hpz-theme";
const LAST_UPDATED = "01 June 2026";

const PLATFORMS = [
  { name: "HustleRewards", blurb: "Loyalty points & member perks across the Group." },
  { name: "HustleConnect", blurb: "Vendor + member messaging & broadcasts." },
  { name: "HustlePlaza",   blurb: "Cooperative-buying marketplace (this site)." },
  { name: "HustleChat",    blurb: "Direct chat with vendors and other members." },
  { name: "HustlePlate",   blurb: "Restaurant & venue directory + bookings." },
  { name: "HustleRecipes", blurb: "Community recipe vault + ingredient orders." },
  { name: "HustleGo",      blurb: "Travel, dine-out, and local-experience deals." },
  { name: "HustleApps",    blurb: "The umbrella mobile experience tying it all together." },
];

export default function HustleGroupTermsPage() {
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "dark";
    return localStorage.getItem(THEME_KEY) || "dark";
  });
  useEffect(() => {
    if (typeof window !== "undefined") localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  return (
    <div className="htg-root" data-theme={theme} data-testid="hustle-terms-root">
      {/* ── Top Bar ─────────────────────────────────────────────── */}
      <div className="htg-topbar">
        <a href="/plaza" className="htg-topbar__brand">
          <img
            src={theme === "light" ? LOGO_LIGHT : LOGO_DARK}
            alt="HustlePlaza"
            className="htg-topbar__logo"
          />
        </a>
        <div className="htg-topbar__right">
          <a href="/plaza" className="htg-back-link" data-testid="hustle-terms-back-plaza">
            <ArrowLeft size={14} /> Back to HustlePlaza
          </a>
          <div className="htg-theme-toggle" role="group" aria-label="Colour mode">
            <button
              className={theme === "light" ? "on" : ""}
              onClick={() => setTheme("light")}
              title="Light mode"
              data-testid="hustle-terms-theme-light"
            >
              <Sun size={16} />
            </button>
            <button
              className={theme === "dark" ? "on" : ""}
              onClick={() => setTheme("dark")}
              title="Dark mode"
              data-testid="hustle-terms-theme-dark"
            >
              <Moon size={16} />
            </button>
          </div>
        </div>
      </div>

      <main className="htg-main">
        {/* ── Hero ──────────────────────────────────────────────── */}
        <header className="htg-hero">
          <span className="htg-pill">Last updated · {LAST_UPDATED}</span>
          <h1>The Hustle Group <em>Cooperative Terms.</em></h1>
          <p className="htg-sub">
            Plain-language terms that govern membership across every product
            in the Hustle Group. No 40-page legal scroll. Read once — they
            apply everywhere we operate.
          </p>
        </header>

        {/* ── 1. The Cooperative ─────────────────────────────────── */}
        <section className="htg-section" data-testid="hustle-terms-section-cooperative">
          <div className="htg-section__head">
            <div className="htg-section__icon"><Users size={20} /></div>
            <div>
              <p className="htg-section__eyebrow">Section 1</p>
              <h2>One membership · Every Hustle platform.</h2>
            </div>
          </div>
          <p>
            When you sign up to any Hustle Group service, you are
            <strong> automatically a member of every platform in the Hustle Group</strong>.
            That includes (and will grow with new platforms we launch):
          </p>
          <ul className="htg-platforms">
            {PLATFORMS.map((p) => (
              <li key={p.name}>
                <strong>{p.name}</strong>
                <span>{p.blurb}</span>
              </li>
            ))}
          </ul>
          <p className="htg-note">
            Your single sign-in unlocks every platform above. You don't need
            separate accounts — and you can use any of them without using all
            of them.
          </p>
        </section>

        {/* ── 2. Communications ──────────────────────────────────── */}
        <section className="htg-section" data-testid="hustle-terms-section-comms">
          <div className="htg-section__head">
            <div className="htg-section__icon"><BellRing size={20} /></div>
            <div>
              <p className="htg-section__eyebrow">Section 2</p>
              <h2>How we'll reach you.</h2>
            </div>
          </div>
          <p>
            By becoming a member you agree to receive communications from the
            Hustle Group via:
          </p>
          <ul className="htg-bullets">
            <li>Phone calls</li>
            <li>SMS / text messages</li>
            <li>Push notifications (to your phone or computer)</li>
            <li>WhatsApp messages or calls</li>
            <li>Email</li>
          </ul>
          <p>
            We use these channels for things you actually need:
            order confirmations, delivery updates, member offers, loyalty-
            reward summaries, and occasional vendor-curated drops we think
            you'll want to know about.
          </p>
        </section>

        {/* ── 3. Privacy & Data ──────────────────────────────────── */}
        <section className="htg-section" data-testid="hustle-terms-section-privacy">
          <div className="htg-section__head">
            <div className="htg-section__icon"><ShieldCheck size={20} /></div>
            <div>
              <p className="htg-section__eyebrow">Section 3</p>
              <h2>The Hustle Group promise on your data.</h2>
            </div>
          </div>
          <div className="htg-promise">
            <p className="htg-promise__big">
              The Hustle Group will <strong>never spam</strong> you, and will
              <strong> never sell your data</strong>.
            </p>
          </div>
          <p>
            Because we operate as a cooperative, we reserve the right to send
            you offers <strong>on behalf of our vendors</strong> — that's the
            engine that makes the cooperative work. The vendor never receives
            your personal data unless <em>you</em> choose to act on the offer
            (e.g. by placing an order, redeeming a code, or messaging them
            directly). Everything else stays inside the cooperative.
          </p>
          <p>
            We store the data we need to run the service safely (orders,
            payments, delivery addresses, communication preferences), and we
            comply with the Australian Privacy Principles under the Privacy
            Act 1988 (Cth).
          </p>
        </section>

        {/* ── 4. Unsubscribe ────────────────────────────────────── */}
        <section className="htg-section" data-testid="hustle-terms-section-unsubscribe">
          <div className="htg-section__head">
            <div className="htg-section__icon"><MailMinus size={20} /></div>
            <div>
              <p className="htg-section__eyebrow">Section 4</p>
              <h2>Unsubscribe — easy, per channel.</h2>
            </div>
          </div>
          <p>
            You can unsubscribe from <strong>any communication channel at any
            time</strong>, independently:
          </p>
          <ul className="htg-bullets">
            <li>Reply <strong>STOP</strong> to any SMS or WhatsApp message.</li>
            <li>Tap <strong>Unsubscribe</strong> at the bottom of any email.</li>
            <li>Turn off push notifications in your phone or browser settings.</li>
            <li>Update your communication preferences in your member account at any time.</li>
            <li>Email <a href="mailto:privacy@hustleplate.com.au">privacy@hustleplate.com.au</a> to request full deletion of your account and data.</li>
          </ul>
          <p>
            Unsubscribing from messages does not cancel your membership —
            you'll keep your account, your loyalty balance, and access to any
            Hustle Group platform.
          </p>
        </section>

        {/* ── 5. Why this matters ───────────────────────────────── */}
        <section className="htg-section htg-section--featured" data-testid="hustle-terms-section-why">
          <div className="htg-section__head">
            <div className="htg-section__icon"><Sparkles size={20} /></div>
            <div>
              <p className="htg-section__eyebrow">Section 5</p>
              <h2>Why a cooperative is good for everyone.</h2>
            </div>
          </div>
          <p>
            Co-operatives work because <strong>members and vendors win
            together</strong>. When thousands of members pool their orders,
            vendors can offer wholesale-tier pricing — and the savings come
            back to the members. The more members, the better the pricing
            for everyone.
          </p>
          <p>
            Being part of the Hustle Group cooperative means: better prices
            on the products you already buy, support for the Australian
            producers and venues we partner with, and a single member account
            that earns rewards across everything we run.
          </p>
        </section>

        {/* ── Contact ─────────────────────────────────────────────── */}
        <section className="htg-contact">
          <h3>Questions?</h3>
          <p>
            Email <a href="mailto:hello@hustleplate.com.au">hello@hustleplate.com.au</a>
            {" "}or write to:
          </p>
          <address>
            Hustle Group Pty Ltd<br/>
            South-East Queensland, Australia
          </address>
        </section>
      </main>

      {/* ── Footer ──────────────────────────────────────────────── */}
      <footer className="htg-foot">
        <p>
          © 2026 Hustle Group ·
          {" "}<a href="/plaza">HustlePlaza</a> ·
          {" "}<a href="/">HustlePlate</a>
        </p>
      </footer>
    </div>
  );
}
